/* Hero, Full and Slim (STYLE §3, §8d.4; HERO1 spec). */
.hero {
  /* Layout follows the hero's own width, not the window's: the Help panel narrows the page beside
     it, and the copy, photos and numbers must re-flow with it (APP-068). */
  container: hero / inline-size;
  position: relative;
  overflow: hidden;
  background: var(--brand-navy-950);
  color: var(--white);
}

/* Collapse button: a 32 px circle in a 44 px target, top right. */
.toggle {
  position: absolute;
  top: 6px;
  right: 10px;
  z-index: 5;
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--white);
  cursor: pointer;
}

.slim .toggle {
  top: 24px;
}

.toggleRing {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border: 1px solid var(--white-a30);
  border-radius: 50%;
  background: var(--navy-a45);
  transition: background-color var(--dur-fast) ease;
}

.toggle[data-hovered] .toggleRing {
  background: var(--white-a12);
}

/* Photos */
.photos {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 4px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 4px;
  width: 56%;
}

.slim .photos {
  left: 0;
  width: auto;
  opacity: 0.25;
}

.tile {
  position: relative;
  display: block;
  overflow: hidden;
}

.kb {
  position: absolute;
  inset: 0;
  display: block;
  transform-origin: 50% 50%;
  will-change: transform;
}

.kbA {
  animation: kbA 24s ease-in-out infinite alternate;
}

.kbB {
  animation: kbB 24s ease-in-out infinite alternate;
}

.kbC {
  animation: kbC 24s ease-in-out infinite alternate;
}

.photo {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo + .photo {
  opacity: 0;
}

/* Cross-fade: every 9 s with a 1.2 s fade, three photos per tile (27 s cycle). */
.xf {
  will-change: opacity;
  animation: xf 27s linear infinite;
}

@keyframes kbA {
  from {
    transform: scale(1) translate3d(0, 0, 0);
  }

  to {
    transform: scale(1.06) translate3d(-1.5%, -1%, 0);
  }
}

@keyframes kbB {
  from {
    transform: scale(1) translate3d(0, 0, 0);
  }

  to {
    transform: scale(1.06) translate3d(1.5%, -1%, 0);
  }
}

@keyframes kbC {
  from {
    transform: scale(1) translate3d(0, 0, 0);
  }

  to {
    transform: scale(1.06) translate3d(-1%, 1%, 0);
  }
}

@keyframes xf {
  0% {
    opacity: 1;
  }

  28.89% {
    opacity: 1;
  }

  33.33% {
    opacity: 0;
  }

  95.56% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}

.wash {
  position: absolute;
  inset: 0;
  /* The full wash (STYLE §6), held solid until just past the copy column's edge (container offset +
     gutter + the copy's 600 px maximum + 24 px) so long copy never reaches the fading part over the
     photos (APP-068). Wide screens keep the board's 40 % / 64 % stops. */
  --copy-edge: calc(
    max(0px, (100% - var(--container-max)) / 2) + var(--content-gutter) + 600px + 24px
  );

  background: linear-gradient(
    90deg,
    var(--brand-navy-950) 0,
    var(--brand-navy-950) max(40%, var(--copy-edge)),
    var(--navy-a55) max(64%, calc(var(--copy-edge) + 12%)),
    var(--navy-a20) 100%
  );
}

.slim .wash {
  background: var(--hero-wash-slim);
}

/* Narrower than 1,024 px (the hero's own width) the copy runs across the photos, where the full wash fades out. There the
   photos become a faint full-width texture under an even wash, so every line keeps 4.5:1 over any
   photo, even a white one (measured at 390–1,000 px; APP-068). Slim already works this way and
   keeps its own rules (they are more specific). */
@container hero (max-width: 1023px) {
  .photos {
    left: 0;
    width: auto;
    opacity: 0.3;
  }

  .wash {
    background: var(--navy-a75);
  }
}

/* Full */
.fullInner {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 28px 32px;
  max-width: var(--container-max);
  min-height: 336px;
  margin: 0 auto;
  padding: 60px var(--content-gutter) 56px;
}

.copy {
  display: flex;
  flex: 1 1 480px;
  flex-direction: column;
  gap: 14px;
  max-width: 600px;
  animation: rise 700ms var(--ease-out) both;
}

.eyebrow {
  margin: 0;
  color: var(--brand-cyan);
}

/* "‹ Console": a quiet way back up a level, above the eyebrow. */
.back {
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  color: var(--text-on-dark-muted);
  font-family: var(--font-ui);
  font-size: 14px;
  letter-spacing: 0.08em;
  text-decoration: none;
  text-transform: uppercase;
}

.back:hover {
  color: var(--text-on-dark);
}

.back:focus-visible,
.backIcon:focus-visible {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: 2px;
}

.backIcon {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin-right: -12px;
  color: var(--text-on-dark-muted);
}

.eyebrowGold {
  color: var(--brand-gold);
}

.title {
  margin: 0;
  font-size: clamp(36px, 4.4vw, 56px);
  line-height: 1.02;
}

/* An award ID wraps as a whole, never at its hyphens (APP-087 §1); words around it wrap as usual. */
.id {
  white-space: nowrap;
}

/* The title and the page's state pill share a row and wrap together. */
.titleRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 14px;
}

.pill {
  flex: none;
}

/* The heading takes focus after each move to a page so screen readers announce it; it isn't a
   control, so it shows no focus ring. */
.title:focus,
.slimTitle:focus {
  outline: none;
}

.sub {
  margin: 0;
  color: var(--text-on-dark-soft);
  font-size: 17px;
  line-height: 1.6;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 6px;
}

.stats {
  display: grid;
  flex: 0 1 560px;
  /* Blocks keep their width; a single one sits at the right edge instead of stretching. */
  grid-template-columns: repeat(auto-fit, minmax(min(230px, 100%), 273px));
  justify-content: end;
  gap: 14px;
}

/* Three numbers sit in one row beside the copy, as the boards show (about 230 px each at 1440):
   three equal columns in 3 × 230 + 2 × 14 px, so copy (480 px minimum) + gap + numbers fit the
   1,320 px row without wrapping. Narrower than 720 px the row has dropped below the copy, and the
   blocks go back to the auto-fit rule above (one or two per row on a phone). */
@container hero (min-width: 720px) {
  .stats[data-count='3'] {
    flex-basis: calc(3 * 230px + 2 * 14px);
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

/* Narrower, the numbers sit under the copy: they take its full width so their edges line up with
   it, rather than one 273 px block hanging at the right (one per row at 390, two when they fit). */
@container hero (max-width: 719px) {
  .stats {
    flex-basis: 100%;
    grid-template-columns: repeat(auto-fit, minmax(min(230px, 100%), 1fr));
  }
}

.glass {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0;
  padding: 20px 22px;
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-md);
  background: var(--glass-bg);
  backdrop-filter: blur(var(--glass-blur));
  animation: rise 700ms var(--ease-out) both;
}

.glass:nth-child(2) {
  animation-delay: 0.1s;
}

.glass:nth-child(3) {
  animation-delay: 0.2s;
}

.glassLabel {
  margin: 0;
  color: var(--text-on-dark-muted);
  font-size: 11px;
}

.glassValue {
  margin: 0;
  font-size: 34px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.toneGold .glassValue,
.toneGold .chipValue {
  color: var(--brand-gold);
}

.toneMuted .glassValue,
.toneMuted .chipValue {
  color: var(--text-on-dark-dim);
}

.glassMeta {
  margin: 0;
  color: var(--text-on-dark-muted);
  font-size: 12px;
  line-height: 1.45;
}

/* A number that is itself the link to what it counts (SA1, X1): the whole tile is the target, an
   arrow at its corner says it goes somewhere, and it brightens on hover. */
.glassLink {
  position: relative;
  color: var(--text-on-dark);
  text-decoration: none;
  cursor: pointer;
  transition: border-color 150ms var(--ease-out);
}

.glassLink .glassLabel {
  padding-right: 28px;
}

.glassLink:hover {
  color: var(--text-on-dark);
}

.glassLink[data-hovered] {
  border-color: var(--white-a42);
}

.glassLink[data-hovered] .glassMeta {
  color: var(--text-on-dark);
}

.glassLink[data-focus-visible] {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: 3px;
}

.glassGo {
  position: absolute;
  top: 18px;
  right: 18px;
  display: inline-flex;
  color: var(--text-on-dark-muted);
  transition: transform 150ms var(--ease-out);
}

.glassLink[data-hovered] .glassGo {
  color: var(--text-on-dark);
  transform: translateX(2px);
}

.glassNotSet {
  border-style: dashed;
  border-color: var(--white-a28);
  background: var(--navy-a42);
}

.notSetValue {
  margin: 0;
  color: var(--text-on-dark-dim);
  font-size: 30px;
  line-height: 1;
}

.notSetMessage {
  margin: 0;
  color: var(--brand-mist-200);
  font-size: 13px;
}

.notSetAction {
  align-self: flex-start;
}

/* Slim */
.slimInner {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 24px;
  max-width: var(--container-max);
  min-height: 92px;
  margin: 0 auto;
  padding: 14px 64px 14px var(--content-gutter);
}

.slimTitleRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  min-width: 0;
}

.slimTitle {
  min-width: 0;
  margin: 0;
  overflow: hidden;
  font-size: 28px;
  line-height: 1.05;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin: 0;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 14px;
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-pill);
  background: var(--glass-bg);
  backdrop-filter: blur(var(--glass-blur));
}

.chipLabel {
  color: var(--text-on-dark-muted);
  font-size: 11px;
  letter-spacing: 0.12em;
}

.chipValue {
  margin: 0;
  font-size: 18px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

/* Slim is one row (DESIGN-SPEC §2, ~88–100 px). On a wide hero the chips take the room the title
   leaves, and a long scoped label ("Executive pool · cash left 2026") breaks onto two short lines
   inside its chip, rather than the chips dropping onto a second row (R1, FINANCE-HOME). A title
   longer than half the bar still wraps as before. */
@container hero (min-width: 1024px) {
  .chips {
    flex: 1 1 0%;
    flex-wrap: nowrap;
    justify-content: flex-end;
    min-width: 50%;
  }

  .chip {
    flex: 0 1 auto;
    min-width: 0;
  }

  .chipLabel {
    line-height: 1.25;
  }

  .chipValue {
    white-space: nowrap;
  }

  .chips > :not(.chip) {
    flex: none;
  }
}

/* The 4 px gradient rule is the progress bar (STYLE §6c). */
.rule {
  position: relative;
  height: 4px;
  overflow: hidden;
  background: var(--gradient-rule);
}

.rule::after {
  content: '';
  position: absolute;
  inset: 0;
  background-image: var(--gradient-rule-flow);
  background-repeat: repeat-x;
  background-size: 50% 100%;
  opacity: 0;
  animation:
    ruleflow 900ms linear 0s 2,
    ruleload 1900ms ease 0s 1 both;
}

.rule[data-busy='true']::after {
  opacity: 1;
  animation: ruleflow 900ms linear infinite;
}

@keyframes ruleflow {
  from {
    background-position: 0% 0;
  }

  to {
    background-position: 100% 0;
  }
}

@keyframes ruleload {
  0% {
    opacity: 1;
  }

  75% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
}

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(14px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

/* Reduced motion: still photos (first of each tile), a still striped bar while busy. */
@media (prefers-reduced-motion: reduce) {
  .kb,
  .xf,
  .copy,
  .glass {
    animation: none !important;
  }

  .glassGo {
    transition: none;
  }

  .glassLink[data-hovered] .glassGo {
    transform: none;
  }

  .rule::after {
    animation: none !important;
    opacity: 0;
  }

  .rule[data-busy='true']::after {
    opacity: 1;
    background-image: repeating-linear-gradient(
      90deg,
      var(--brand-cyan) 0 24px,
      var(--brand-gold) 24px 48px
    );
    background-size: auto;
  }
}

:global(html[data-motion='reduced']) .kb,
:global(html[data-motion='reduced']) .xf,
:global(html[data-motion='reduced']) .copy,
:global(html[data-motion='reduced']) .glass {
  animation: none !important;
}

:global(html[data-motion='reduced']) .glassGo {
  transition: none;
}

:global(html[data-motion='reduced']) .glassLink[data-hovered] .glassGo {
  transform: none;
}

:global(html[data-motion='reduced']) .rule::after {
  animation: none !important;
  opacity: 0;
}

:global(html[data-motion='reduced']) .rule[data-busy='true']::after {
  opacity: 1;
  background-image: repeating-linear-gradient(
    90deg,
    var(--brand-cyan) 0 24px,
    var(--brand-gold) 24px 48px
  );
  background-size: auto;
}

/* A work page's steps (SA2, SA3): numbered marks joined by a rule; done ones say so in words. */
.glassSteps {
  min-width: min(100%, 400px);
}

.stepList {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 0;
  align-items: center;
  margin: 0;
  padding: 0;
  list-style: none;
}

.step {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  color: var(--text-on-dark-muted);
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: var(--fw-semibold);
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.step + .step::before {
  width: 24px;
  height: 1px;
  margin: 0 12px;
  background: var(--glass-border);
  content: '';
}

.stepMark {
  display: grid;
  flex: none;
  place-items: center;
  width: 26px;
  height: 26px;
  border: 1px solid var(--white-a45);
  border-radius: 50%;
  font-family: var(--font-ui);
  font-size: 12px;
  letter-spacing: 0;
}

.stepDone {
  color: var(--text-on-dark);
}

.stepDone .stepMark {
  border-color: var(--status-done-ink);
  background: var(--status-done-ink);
  color: var(--white);
}

.stepCurrent {
  color: var(--text-on-dark);
}

.stepCurrent .stepMark {
  border-color: var(--brand-cyan);
  background: var(--brand-cyan);
  color: var(--brand-navy-950);
}

.stepLabel {
  white-space: nowrap;
}
